import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Loading from '../Loading';
import RecipientSelect from '../components/RecipientSelect';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';
import QuoteField from './QuoteField';
import QuoteSortableLineRow from './QuoteSortableLineRow';
import {
  newQuoteLineId,
  applyQuoteLineIdRemap,
  ensureLineIdsInQuotePatch,
  flattenSnapshotKeys,
  getNestedIfOwn,
  NESTED_UNSET,
  mergeQuoteLines,
  quoteLineSortId,
  assignQuoteLinePositions,
} from './quoteSnapshotUtils';

const QUOTE_PUBLIC_SHOW_BASE = 'https://www.proasysinc.com/quotes/show';

const searchChipTransition = {
  opacity: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
  y: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
};

/**
 * Shallow-deep merge of plain-object sources. Later sources win. Arrays and
 * primitives replace; plain objects merge recursively. Null/undefined values
 * in later layers do NOT overwrite earlier values.
 */
function deepMerge(...sources) {
  const isPlainObject = (v) =>
    v != null && typeof v === 'object' && !Array.isArray(v);
  return sources.reduce((acc, src) => {
    if (!isPlainObject(src)) return acc;
    const out = { ...acc };
    for (const key of Object.keys(src)) {
      const next = src[key];
      if (next == null) continue;
      const prev = out[key];
      out[key] = isPlainObject(prev) && isPlainObject(next)
        ? deepMerge(prev, next)
        : next;
    }
    return out;
  }, {});
}

/** Deep equality for plain JSON-like data (objects, arrays, primitives). */
function valuesEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!valuesEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (let i = 0; i < keysA.length; i++) {
    const k = keysA[i];
    if (!Object.prototype.hasOwnProperty.call(b, k) || !valuesEqual(a[k], b[k])) return false;
  }
  return true;
}

/**
 * Returns a new patch containing only values that differ from `quoteVal`.
 * When a nested object has any changed fields, its `id` is always preserved
 * (pulled from the patch if present, otherwise from the corresponding stored
 * object) so Rails can identify the record in nested-attributes updates.
 */
function pruneDiff(patchVal, quoteVal) {
  if (patchVal == null || typeof patchVal !== 'object') {
    return Object.is(patchVal, quoteVal) ? undefined : patchVal;
  }

  if (Array.isArray(patchVal)) {
    if (valuesEqual(patchVal, quoteVal)) return undefined;
    const qArr = Array.isArray(quoteVal) ? quoteVal : [];
    const quoteById = new Map();
    qArr.forEach((item) => {
      if (item != null && typeof item === 'object' && item.id != null) {
        quoteById.set(String(item.id), item);
      }
    });
    const mapped = patchVal.map((item, i) => {
      let stored = qArr[i];
      if (item != null && typeof item === 'object' && item.id != null && quoteById.has(String(item.id))) {
        stored = quoteById.get(String(item.id));
      }
      return pruneDiff(item, stored);
    });
    const filtered = mapped.filter((v) => v !== undefined);
    if (filtered.length === 0) return undefined;
    return filtered;
  }

  const out = {};
  for (const key of Object.keys(patchVal)) {
    const qv = quoteVal != null && typeof quoteVal === 'object' && !Array.isArray(quoteVal)
      ? quoteVal[key]
      : undefined;
    const pruned = pruneDiff(patchVal[key], qv);
    if (pruned !== undefined) out[key] = pruned;
  }

  if (Object.keys(out).length === 0) return undefined;

  // Preserve the record id whenever anything beneath it changed so the server
  // can target the existing row (otherwise an unchanged id gets pruned out and
  // Rails would treat it as a new record).
  if (out.id == null) {
    const id = patchVal.id != null
      ? patchVal.id
      : (quoteVal != null && typeof quoteVal === 'object' && !Array.isArray(quoteVal)
          ? quoteVal.id
          : undefined);
    if (id != null) out.id = id;
  }

  return out;
}

/** Display money with 2 decimals. Empty in → empty out. */
function formatPriceTwoDecimals(raw) {
  if (raw === '' || raw == null) return '';
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
  if (!Number.isFinite(n)) return String(raw);
  return n.toFixed(2);
}

function parseQuantityRaw(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function hasUnitPriceRaw(rawP) {
  if (rawP === '' || rawP == null) return false;
  return Number.isFinite(Number(String(rawP).replace(/,/g, '')));
}

function parseRecipientsCsv(raw) {
  if (raw == null || raw === '') return [];
  const s = typeof raw === 'string' ? raw : String(raw);
  return s.split(',').map((e) => e.trim()).filter(Boolean);
}

/** DB default is true; treat missing / empty form state as checked. */
function isQuoteBlindCopyChecked(raw) {
  if (raw === '' || raw == null) return true;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

/**
 * Hold a new address locally until street/city/state/zip are all present.
 * Expand partial patches to the full merged address so sibling fields are not dropped.
 * Once the quote owns an address, partial updates are allowed.
 */
function addressFieldPresent(value) {
  return value != null && String(value).trim() !== '';
}

function isAddressCompleteForCreate(address) {
  if (!address || typeof address !== 'object') return false;
  return ['street', 'city', 'state', 'zip'].every((key) => addressFieldPresent(address[key]));
}

function prepareAddressForPersist(patch, quote = {}) {
  if (!patch || patch.address == null || typeof patch.address !== 'object') {
    return patch;
  }
  const merged = deepMerge({}, quote.address || {}, patch.address);
  const quoteHasAddress = quote.address?.id != null;
  if (!quoteHasAddress && !isAddressCompleteForCreate(merged)) {
    const { address, ...rest } = patch;
    return rest;
  }
  return { ...patch, address: merged };
}

const QuoteWizard = (props) => {
  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchField, setSearchField] = useState('');
  const activeQuote = props.activeQuote || {};
  const [unsynced, setUnsynced] = useState({});
  const [previewFieldValues, setPreviewFieldValues] = useState({});
  const [productSearchLineIndex, setProductSearchLineIndex] = useState(0);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipientModalIsSendFlow, setRecipientModalIsSendFlow] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [unitPriceFocusLine, setUnitPriceFocusLine] = useState(null);
  const [lineTotalFocusLine, setLineTotalFocusLine] = useState(null);
  const [focusModelLineIndex, setFocusModelLineIndex] = useState(null);
  const [focusedField, setFocusedField] = useState(null); // so we dont show the saving flash while field is being edited
  const [savedFields, setSavedFields] = useState([]); // used to flash the saved indicator on updated fields

  const workingQuoteLines = useMemo(
    () => mergeQuoteLines(activeQuote, unsynced),
    [activeQuote, unsynced]
  );

  const visibleLineEntries = useMemo(
    () => workingQuoteLines
      .map((line, index) => ({
        line,
        index,
        sortId: quoteLineSortId(line, index),
      }))
      .filter(({ line }) => !line?._destroy),
    [workingQuoteLines]
  );

  const lineSortIds = useMemo(
    () => visibleLineEntries.map((e) => e.sortId),
    [visibleLineEntries]
  );

  const lineDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  // Mirror of `unsynced` for timer callbacks (state closures would be stale).
  // Kept in sync by writing both state and ref from every mutation site.
  const unsyncedRef = useRef({});
  const quoteUpdateTimerRef = useRef(null);
  const savedIndicatorTimerRef = useRef(null);
  const lineModelInputRefs = useRef({});

  const writeUnsynced = (next) => {
    unsyncedRef.current = next;
    setUnsynced(next);
  }

  const quoteIdentity = activeQuote?.id ?? 'new';
  useEffect(() => {
    writeUnsynced({});
    setPreviewFieldValues({});
    setSearchString('');
    setSearchResults([]);
    setSearchField('');
    setProductSearchLineIndex(0);
    setRecipientModalOpen(false);
    setRecipientModalIsSendFlow(false);
    setSelectedRecipients([]);
    setUnitPriceFocusLine(null);
    setLineTotalFocusLine(null);
    setFocusModelLineIndex(null);
    clearTimeout(savedIndicatorTimerRef.current);
    savedIndicatorTimerRef.current = null;
    setSavedFields([]);
  }, [quoteIdentity]);

  useLayoutEffect(() => {
    if (focusModelLineIndex == null) return;
    const idx = focusModelLineIndex;
    const el = lineModelInputRefs.current[idx];
    if (el && document.body.contains(el)) {
      el.focus();
    }
    setFocusModelLineIndex(null);
  }, [focusModelLineIndex]);

  const setAutocomplete = (fieldId, value, productLineIndex = 0) => {
    setSearchField(fieldId);
    setSearchString(value);
    if (fieldId === 'product') {
      setProductSearchLineIndex(productLineIndex);
    }

    if (value && value.length > 2) {
      let records = [];
      if (fieldId === 'customer') {
        records = props.customers.filter(record => record.name.toLowerCase().includes(value.toLowerCase()))
      } else {
        records = props.products.filter(record => record.model.toLowerCase().concat(` ${record.description}`).includes(value.toLowerCase()))
      }
      setSearchResults(records.slice(0, 10));
    } else {
      setSearchResults([]);

    }
  }

  const patchBuilder = (patch, quote = activeQuote) => {
    if (!patch || typeof patch !== 'object') return patch;
    return pruneDiff(patch, quote) || {};
  };

  // Walks a dot-separated path (e.g. "address.street") into a plain object.
  const getNested = (obj, path) => {
    if (obj == null) return undefined;
    if (!path.includes('.')) return obj[path];
    return path.split('.').reduce(
      (acc, key) => (acc == null ? undefined : acc[key]),
      obj
    );
  }

  // Immutably writes `value` at a dot-separated `path` in `obj`, cloning only
  // the containers along the path so unrelated branches keep referential equality.
  // Numeric path segments (e.g. "quote_lines.0.price") create/preserve arrays.
  const setNested = (obj, path, value) => {
    const isIndex = (k) => /^\d+$/.test(k);
    const cloneContainer = (v, wantsArray) => {
      if (Array.isArray(v)) return [...v];
      if (v != null && typeof v === 'object') return { ...v };
      return wantsArray ? [] : {};
    }
    const keys = path.split('.');
    const root = cloneContainer(obj, isIndex(keys[0]));
    let cursor = root;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      cursor[key] = cloneContainer(cursor[key], isIndex(keys[i + 1]));
      cursor = cursor[key];
    }
    cursor[keys[keys.length - 1]] = value;
    return root;
  }

  const handleInputChange = (fieldId, value) => {
    const lineMatch = fieldId.match(/^quote_lines\.(\d+)\.(.+)$/);
    if (lineMatch) {
      const idx = Number(lineMatch[1]);
      const key = lineMatch[2];
      const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
      const next = merged.map((line, i) => (i === idx ? { ...line, [key]: value } : line));
      writeUnsynced({ ...unsyncedRef.current, quote_lines: next });
      return;
    }
    writeUnsynced(setNested(unsyncedRef.current, fieldId, value));
  }

  // Unsynced wins (including explicit `null` clears), then hover preview, then working lines.
  const getInputValue = (fieldId) => {
    const lineMatch = fieldId.match(/^quote_lines\.(\d+)\.(.+)$/);
    if (lineMatch) {
      const idx = Number(lineMatch[1]);
      const key = lineMatch[2];
      const u = getNestedIfOwn(unsynced, fieldId);
      if (u !== NESTED_UNSET) return u == null || u === '' ? '' : u;
      const p = getNestedIfOwn(previewFieldValues, fieldId);
      if (p !== NESTED_UNSET) return p == null || p === '' ? '' : p;
      const line = workingQuoteLines[idx];
      if (line && Object.prototype.hasOwnProperty.call(line, key)) {
        const v = line[key];
        return v == null || v === '' ? '' : v;
      }
      return '';
    }
    for (const source of [unsynced, previewFieldValues, activeQuote]) {
      const v = getNestedIfOwn(source, fieldId);
      if (v !== NESTED_UNSET) return v == null || v === '' ? '' : v;
    }
    return '';
  }

  const recipientCustomerContacts = useMemo(() => {
    const id = unsynced.customer_id ?? activeQuote.customer_id;
    if (id == null || id === '') return [];
    const cust = (props.customers || []).find((c) => String(c.id) === String(id));
    return Array.isArray(cust?.contacts) ? cust.contacts : [];
  }, [props.customers, activeQuote.customer_id, unsynced.customer_id]);

  const isAdmin = props.user?.admin === true;

  const applyProasysSignatureDefault = (patch) => {
    if (isAdmin || !props.user?.employee?.id) return patch;
    const employeeId = props.user.employee.id;
    if (activeQuote.id) {
      const existing = activeQuote.proasys_signature;
      if (existing != null && existing !== '') return patch;
    }
    return { ...patch, proasys_signature: employeeId };
  };

  const selectedProasysSignerId = getInputValue('proasys_signature') || props.user?.employee?.id || '';
  const proasysSignerOptions = useMemo(() => {
    const options = [...(props.quoteEmployees || [])];
    const selectedId = selectedProasysSignerId;
    if (selectedId && !options.some((emp) => String(emp.id) === String(selectedId))) {
      const current = props.user?.employee;
      options.unshift({
        id: selectedId,
        name:
          current && String(current.id) === String(selectedId)
            ? current.name
            : `Employee #${selectedId}`,
      });
    }
    return options;
  }, [props.quoteEmployees, selectedProasysSignerId, props.user?.employee]);

  const quoteFieldProps = (fieldId) => {
    // Incomplete new addresses are held locally until street/city/state/zip are set.
    let savingUnsynced = unsynced;
    if (fieldId.startsWith('address.')) {
      const mergedAddress = deepMerge({}, activeQuote.address || {}, unsynced.address || {});
      const canPersistAddress =
        activeQuote.address?.id != null || isAddressCompleteForCreate(mergedAddress);
      if (!canPersistAddress && unsynced.address) {
        savingUnsynced = { ...unsynced };
        delete savingUnsynced.address;
      }
    }
    return {
      fieldId,
      focusedField,
      unsynced: savingUnsynced,
      savedFields,
      setFocusedField,
    };
  };

  const finishPersist = (snapshot, patch, saved, idRemap, onSaved) => {
    let latest = unsyncedRef.current;
    if (idRemap && Object.keys(idRemap).length && Array.isArray(latest.quote_lines)) {
      latest = {
        ...latest,
        quote_lines: applyQuoteLineIdRemap(latest.quote_lines, idRemap),
      };
    }
    // Only clear keys that were actually sent. Incomplete address may stay in
    // unsynced while other fields persist successfully.
    const persistedTopKeys = new Set(
      Object.keys(patch || {}).filter((k) => k !== 'id')
    );
    const remaining = Object.keys(latest).reduce((acc, key) => {
      if (!persistedTopKeys.has(key)) {
        acc[key] = latest[key];
        return acc;
      }
      if (!valuesEqual(latest[key], snapshot[key])) acc[key] = latest[key];
      return acc;
    }, {});
    writeUnsynced(remaining);

    if (patch && Object.keys(patch).length > 0) {
      clearTimeout(savedIndicatorTimerRef.current);
      setSavedFields(flattenSnapshotKeys(patch));
      savedIndicatorTimerRef.current = setTimeout(() => {
        setSavedFields([]);
        savedIndicatorTimerRef.current = null;
      }, 3000);
    }

    if (onSaved) onSaved(saved, idRemap);
  };

  /**
   * Persists the draft: incremental patch when the quote already exists,
   * otherwise sends a full create payload derived from activeQuote + unsynced.
   */
  const persistQuote = (onSaved) => {
    clearTimeout(quoteUpdateTimerRef.current);
    quoteUpdateTimerRef.current = null;
    const snapshot = unsyncedRef.current;
    const hasUnsynced = snapshot && Object.keys(snapshot).length > 0;

    if (activeQuote.id) {
      if (!hasUnsynced) {
        if (onSaved) onSaved(null);
        return;
      }
      const patch = applyProasysSignatureDefault(
        prepareAddressForPersist(
          ensureLineIdsInQuotePatch(patchBuilder(snapshot)),
          activeQuote
        )
      );
      if (!patch || Object.keys(patch).length === 0) {
        if (onSaved) onSaved(null);
        return;
      }
      // pruneDiff only sends changed fields; the API needs quote id to update (not create).
      patch.id = activeQuote.id;
      props.updateQuote(patch, props.authToken, (saved, idRemap) => {
        finishPersist(snapshot, patch, saved, idRemap, onSaved);
      });
      return;
    }

    const merged = deepMerge({}, activeQuote, snapshot);
    const patch = applyProasysSignatureDefault(
      prepareAddressForPersist(
        ensureLineIdsInQuotePatch(pruneDiff(merged, {}) || {}),
        activeQuote
      )
    );
    if (!patch || Object.keys(patch).length === 0) {
      if (onSaved) onSaved(null);
      return;
    }
    props.updateQuote(patch, props.authToken, (saved, idRemap) => {
      finishPersist(snapshot, patch, saved, idRemap, onSaved);
    });
  }

  const cancelPendingFlush = () => {
    clearTimeout(quoteUpdateTimerRef.current);
    quoteUpdateTimerRef.current = null;
  }

  const handleInputBlur = () => {
    cancelPendingFlush();
    setFocusedField(null);
    quoteUpdateTimerRef.current = setTimeout(() => persistQuote(), 1000);
  }

  /** When unit price is set, keep +line_total+ equal to qty × unit. */
  const syncLineTotalFromUnitPrice = (lineIndex) => {
    const pRaw = getInputValue(`quote_lines.${lineIndex}.price`);
    if (!hasUnitPriceRaw(pRaw)) return;
    const pn = Number(String(pRaw).replace(/,/g, ''));
    if (!Number.isFinite(pn)) return;
    const qn = parseQuantityRaw(getInputValue(`quote_lines.${lineIndex}.quantity`));
    if (qn == null || qn <= 0) return;
    handleInputChange(`quote_lines.${lineIndex}.line_total`, (qn * pn).toFixed(2));
  };

  /** Snap stored line total on focus; seed from qty×unit when empty but unit is set. */
  const syncLineTotalFormatOnFocus = (lineIndex) => {
    cancelPendingFlush();
    setLineTotalFocusLine(lineIndex);
    const rawLt = getInputValue(`quote_lines.${lineIndex}.line_total`);
    if (rawLt !== '' && rawLt != null) {
      const n = Number(String(rawLt).replace(/,/g, ''));
      if (Number.isFinite(n)) {
        const fixed = n.toFixed(2);
        if (String(rawLt).replace(/,/g, '').trim() !== fixed) {
          handleInputChange(`quote_lines.${lineIndex}.line_total`, fixed);
        }
      }
      return;
    }
    const rawP = getInputValue(`quote_lines.${lineIndex}.price`);
    if (!hasUnitPriceRaw(rawP)) return;
    const pn = Number(String(rawP).replace(/,/g, ''));
    if (!Number.isFinite(pn)) return;
    const qn = parseQuantityRaw(getInputValue(`quote_lines.${lineIndex}.quantity`));
    if (qn == null || qn <= 0) return;
    handleInputChange(`quote_lines.${lineIndex}.line_total`, (qn * pn).toFixed(2));
  };

  /** Clear unit price only; leave +line_total+ unchanged (line amount mode). */
  const clearUnitPrice = (lineIndex) => {
    handleInputChange(`quote_lines.${lineIndex}.price`, null);
    setPreviewFieldValues((prev) => {
      const lines = prev?.quote_lines;
      if (!Array.isArray(lines) || lines[lineIndex] == null) return prev;
      const nextLines = lines.map((line, i) =>
        i === lineIndex ? { ...(line || {}), price: null } : { ...(line || {}) }
      );
      return {
        ...prev,
        quote_lines: nextLines,
        previewProductHasListPrice: false,
      };
    });
  };

  /** Snap stored price to xx.xx on focus so it matches the line total display. */
  const syncUnitPriceFormatOnFocus = (lineIndex) => {
    cancelPendingFlush();
    setUnitPriceFocusLine(lineIndex);
    const raw = getInputValue(`quote_lines.${lineIndex}.price`);
    if (raw !== '' && raw != null) {
      const n = Number(String(raw).replace(/,/g, ''));
      if (Number.isFinite(n)) {
        const fixed = n.toFixed(2);
        if (String(raw).replace(/,/g, '').trim() !== fixed) {
          handleInputChange(`quote_lines.${lineIndex}.price`, fixed);
        }
      }
      syncLineTotalFromUnitPrice(lineIndex);
    }
  };

  const handleLineDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    cancelPendingFlush();

    const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
    const visible = merged
      .map((line, index) => ({ line, sortId: quoteLineSortId(line, index) }))
      .filter(({ line }) => !line?._destroy);
    const from = visible.findIndex((e) => e.sortId === String(active.id));
    const to = visible.findIndex((e) => e.sortId === String(over.id));
    if (from < 0 || to < 0) return;

    const reordered = arrayMove(
      visible.map((e) => e.line),
      from,
      to
    );
    const destroyed = merged.filter((line) => line?._destroy);
    const next = assignQuoteLinePositions([...reordered, ...destroyed]);
    writeUnsynced({ ...unsyncedRef.current, quote_lines: next });
    persistQuote();
  };

  const addQuoteLine = () => {
    cancelPendingFlush();
    const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
    const visible = merged.filter((line) => !line?._destroy);
    const destroyed = merged.filter((line) => line?._destroy);
    const next = assignQuoteLinePositions([
      ...visible,
      {
        id: newQuoteLineId(),
        quantity: '',
        price: '',
        line_total: '',
        description: '',
        model: '',
      },
      ...destroyed,
    ]);
    const newIdx = visible.length;
    writeUnsynced({ ...unsyncedRef.current, quote_lines: next });
    setFocusModelLineIndex(newIdx);
    persistQuote();
  };

  const removeQuoteLine = (index) => {
    cancelPendingFlush();
    const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
    const visible = merged.filter((line) => !line?._destroy);
    if (visible.length <= 1) {
      const row = visible[0] || {};
      writeUnsynced({
        ...unsyncedRef.current,
        quote_lines: assignQuoteLinePositions([{
          ...row,
          id: row.id,
          model: '',
          description: '',
          quantity: '',
          price: '',
          line_total: '',
          quotable_product_id: null,
        }]),
      });
      persistQuote();
      return;
    }
    const targetIndex = visibleLineEntries[index]?.index;
    if (targetIndex == null) return;
    const next = merged.map((line, i) =>
      i === targetIndex ? { ...line, id: line.id, _destroy: true } : { ...line }
    );
    writeUnsynced({ ...unsyncedRef.current, quote_lines: assignQuoteLinePositions(next) });
    persistQuote();
  };

  const applyRecipientsFromModal = () => {
    cancelPendingFlush();
    handleInputChange('recipients', selectedRecipients.join(','));
    setRecipientModalOpen(false);
    setRecipientModalIsSendFlow(false);
    setSelectedRecipients([]);
    quoteUpdateTimerRef.current = setTimeout(() => persistQuote(), 0);
  };

  const openRecipientsPicker = () => {
    cancelPendingFlush();
    setRecipientModalIsSendFlow(false);
    setSelectedRecipients(parseRecipientsCsv(getInputValue('recipients')));
    setRecipientModalOpen(true);
  };

  const removeRecipient = (email) => {
    cancelPendingFlush();
    const next = parseRecipientsCsv(getInputValue('recipients')).filter((e) => e !== email);
    handleInputChange('recipients', next.join(','));
    quoteUpdateTimerRef.current = setTimeout(() => persistQuote(), 0);
  };

  const recipientContactName = (email) => {
    const contact = recipientCustomerContacts.find(
      (c) => c.email && c.email.toLowerCase() === email.toLowerCase()
    );
    if (!contact) return null;
    return [contact.fname, contact.lname].filter(Boolean).join(' ').trim() || null;
  };

  const recipientsList = parseRecipientsCsv(getInputValue('recipients'));

  const completeSend = () => {
    if (selectedRecipients.length === 0) {
      window.alert('Select at least one recipient.');
      return;
    }
    const recipientsStr = selectedRecipients.join(',');
    const qid = activeQuote.id;
    props.updateQuote(
      {
        id: qid,
        recipients: recipientsStr,
        blind_copy: isQuoteBlindCopyChecked(getInputValue('blind_copy')),
      },
      props.authToken,
      (saved) => {
        const hash = (saved && saved.hash_id) || activeQuote.hash_id;
        if (!hash) {
          window.alert('Could not send this quote. Try again after it finishes saving.');
          return;
        }
        props.submitQuote(props.authToken, hash, () => {
          setRecipientModalOpen(false);
          setRecipientModalIsSendFlow(false);
          setSelectedRecipients([]);
          props.initializeQuotes(props.authToken);
        });
      }
    );
  };

  const renderSearchResults = (fieldId) => {
    const visible =
      searchString?.length > 2 &&
      searchResults?.length > 0 &&
      searchField === fieldId;

    const handleChipClick = (result) => {
      cancelPendingFlush();
      let chipPatch = null;
      if (fieldId === 'customer') {
        chipPatch = {
          customer_name: result.name,
          customer_id: result.id,
          address: { ...result.address },
        };
      } else if (fieldId === 'product') {
        const idx = productSearchLineIndex;
        const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
        const targetIndex = visibleLineEntries[idx]?.index ?? idx;
        const base = merged[targetIndex] || {};
        const rawQ = base.quantity;
        const qtyBlank =
          rawQ === '' ||
          rawQ == null ||
          (typeof rawQ === 'string' && rawQ.trim() === '');
        const qtyNum = qtyBlank ? 1 : Number(rawQ);
        const qtyOk = Number.isFinite(qtyNum) && qtyNum > 0;
        const nextLine = {
          ...base,
          id: base.id,
          quotable_product_id: result.id,
          model: result.model,
          description: result.description,
        };
        if (qtyBlank) {
          nextLine.quantity = '1';
        }
        if (result.list_price != null) {
          const pu = Number(result.list_price);
          if (Number.isFinite(pu)) {
            const pFixed = pu.toFixed(2);
            nextLine.price = pFixed;
            if (qtyOk) {
              nextLine.line_total = (qtyNum * pu).toFixed(2);
            }
          }
        }
        merged[targetIndex] = nextLine;
        chipPatch = { quote_lines: assignQuoteLinePositions(merged) };
      }
      if (!chipPatch) return;
      writeUnsynced(deepMerge(unsyncedRef.current, chipPatch));
      persistQuote();
      setSearchString('');
      setSearchField('');
      setSearchResults([]);
      setPreviewFieldValues({});
    };

    const handleChipHover = (result) => {
      if (!result) {
        setPreviewFieldValues({});
        return;
      }
      if (fieldId === 'customer') {
        setPreviewFieldValues({ customer_name: result.name, address: result.address });
        return;
      }
      if (fieldId === 'product') {
        const idx = productSearchLineIndex;
        const merged = mergeQuoteLines(activeQuote, unsyncedRef.current);
        const targetIndex = visibleLineEntries[idx]?.index ?? idx;
        const base = merged[targetIndex] || {};
        const qty = Number(base.quantity);
        const qtyOk = Number.isFinite(qty) && qty > 0;
        const overlay = {
          ...base,
          id: base.id,
          quotable_product_id: result.id,
          model: result.model,
          description: result.description,
        };
        let previewHasListPrice = false;
        let previewHasLineTotalPreview = false;
        if (result.list_price != null) {
          const pu = Number(result.list_price);
          if (Number.isFinite(pu)) {
            previewHasListPrice = true;
            overlay.price = pu.toFixed(2);
            if (qtyOk) {
              overlay.line_total = (qty * pu).toFixed(2);
              previewHasLineTotalPreview = true;
            }
          }
        }
        const previewLines = merged.map((line, i) =>
          i === idx ? { ...(line || {}), ...overlay } : { ...(line || {}) }
        );
        setPreviewFieldValues({
          productPreviewLine: idx,
          quote_lines: previewLines,
          previewProductHasListPrice: previewHasListPrice,
          previewProductHasLineTotalPreview: previewHasLineTotalPreview,
        });
      }
    };

    return (
      <div className="search-results-container">
        <AnimatePresence initial={false}>
          {visible &&
            searchResults.map((result) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={searchChipTransition}
                className="search-result"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleChipClick(result)}
                onMouseEnter={() => handleChipHover(result)}
                onMouseLeave={() => handleChipHover(null)}
              >
                {result.name || `${result.model} ${result.description}`}
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="quote-wizard">
      {/* <div className="quote-wizard-loading">
        <Loading loading={props.loading} loadingMessage={props.loadingMessage || 'Saving quote...'} location={{top: '0px', left: '25px'}} />
      </div> */}
      <div className="quote-wizard-watermark"></div>
      <h1 className="quote-wizard-title">Quote Wizard</h1>

      <div className="quote-wizard-body">
        <form
          className="quote-form" 
          autoComplete="new-password"
          onSubmit={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
        >
          <div className="quote-form-row quote-form-section-divider quote-form-section-divider--first">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Customer &amp; address</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>
          <div className="quote-form-header quote-form-panel">
            <p className="quote-form-header-date">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <div className="quote-form-row">
              <QuoteField {...quoteFieldProps('customer_name')} className="quote-field-wrap--grow">
                <input
                  className={`quote-form-control-bordered ${previewFieldValues?.customer_name ? 'replace-field-value' : ''}`}
                  autoComplete="new-password"
                  type="text"
                  placeholder="Enter customer name"
                  value={getInputValue('customer_name')}
                  onChange={(e) => {
                    setAutocomplete('customer', e.target.value);
                    handleInputChange('customer_name', e.target.value);
                  }}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
            </div>

            {renderSearchResults('customer')}

            <div className="quote-form-row">
              <QuoteField {...quoteFieldProps('address.street')} className="quote-field-wrap--grow">
                <input
                  className={`quote-form-control-bordered ${previewFieldValues?.address?.street ? 'replace-field-value' : ''}`}
                  autoComplete="new-password"
                  type="text"
                  placeholder="Street"
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  onBlur={handleInputBlur}
                  value={getInputValue('address.street')}
                />
              </QuoteField>
            </div>
            <div className="quote-form-row">
              <QuoteField {...quoteFieldProps('address.city')} className="quote-field-wrap--address-city">
                <input
                  className={`quote-form-control-bordered ${previewFieldValues?.address?.city ? 'replace-field-value' : ''}`}
                  autoComplete="new-password"
                  type="text"
                  placeholder="City"
                  value={getInputValue('address.city')}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
              <QuoteField {...quoteFieldProps('address.state')} className="quote-field-wrap--address-sm">
                <input
                  className={`quote-form-control-bordered ${previewFieldValues?.address?.state ? 'replace-field-value' : ''}`}
                  autoComplete="new-password"
                  type="text"
                  placeholder="State"
                  value={getInputValue('address.state')}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
              <QuoteField {...quoteFieldProps('address.zip')} className="quote-field-wrap--address-sm">
                <input
                  className={`quote-form-control-bordered ${previewFieldValues?.address?.zip ? 'replace-field-value' : ''}`}
                  autoComplete="new-password"
                  type="text"
                  placeholder="Zip"
                  value={getInputValue('address.zip')}
                  onChange={(e) => handleInputChange('address.zip', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
            </div>
          </div>

          <div className="quote-form-row quote-form-section-divider">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Recipients</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>

          <div className="quote-form-panel quote-form-recipients-panel">
            <QuoteField {...quoteFieldProps('recipients')} className="quote-field-wrap--block">
              <div className="quote-form-recipients">
                {recipientsList.length === 0 ? (
                  <button
                    type="button"
                    className="quote-form-recipients-add"
                    onClick={openRecipientsPicker}
                  >
                    <span className="quote-form-recipients-add-glyph" aria-hidden="true">+</span>
                    Add recipients
                  </button>
                ) : (
                  <>
                    <span className="quote-form-recipients-count">
                      {recipientsList.length} recipient{recipientsList.length === 1 ? '' : 's'}
                    </span>
                    <ul className="quote-form-recipient-list">
                      {recipientsList.map((email, i) => {
                        const name = recipientContactName(email);
                        return (
                          <li
                            key={`${email}-${i}`}
                            className="quote-form-recipient-chip"
                            title={email}
                          >
                            {name && (
                              <span className="quote-form-recipient-chip-name">{name}</span>
                            )}
                            <span className="quote-form-recipient-chip-email">{email}</span>
                            <button
                              type="button"
                              className="quote-form-recipient-chip-remove"
                              onClick={() => removeRecipient(email)}
                              aria-label={`Remove ${email}`}
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                      <li className="quote-form-recipient-add-item">
                        <button
                          type="button"
                          className="quote-form-recipient-add-pill"
                          onClick={openRecipientsPicker}
                        >
                          <span className="quote-form-recipients-add-glyph" aria-hidden="true">+</span>
                          <span>Add</span>
                        </button>
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </QuoteField>
            <QuoteField {...quoteFieldProps('blind_copy')} className="quote-field-wrap--block">
              <label className="quote-form-recipient-bcc" htmlFor="quote-blind-copy">
                <input
                  id="quote-blind-copy"
                  type="checkbox"
                  checked={isQuoteBlindCopyChecked(getInputValue('blind_copy'))}
                  onChange={(e) => {
                    handleInputChange('blind_copy', e.target.checked);
                    cancelPendingFlush();
                    quoteUpdateTimerRef.current = setTimeout(() => persistQuote(), 400);
                  }}
                />
                <span className="quote-form-recipient-bcc-label">Blind copy (Bcc)</span>
                <span className="quote-form-recipient-bcc-hint">
                  Recipients can{getInputValue('blind_copy') ? '\'t' : ""} see each other's addresses
                </span>
              </label>
            </QuoteField>
          </div>

          <div className="quote-form-row quote-form-section-divider">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Cover message</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>

          <div className="quote-form-panel">
            <label className="quote-form-subsection-label" htmlFor="quote-wizard-subject">
              Subject <span className="quote-form-label-optional">(optional)</span>
            </label>
            <QuoteField {...quoteFieldProps('subject')} className="quote-field-wrap--block">
              <input
                id="quote-wizard-subject"
                className="quote-form-control-bordered"
                type="text"
                placeholder="e.g. Quotation for analytical instruments"
                value={getInputValue('subject')}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                onBlur={handleInputBlur}
              />
            </QuoteField>
            <label className="quote-form-subsection-label" htmlFor="quote-wizard-body">
              Message
            </label>
            <QuoteField {...quoteFieldProps('body')} className="quote-field-wrap--block">
              <textarea
                id="quote-wizard-body"
                className="quote-form-control-bordered quote-form-control-bordered--tall"
                placeholder="Introductory text shown above line items on the quote."
                rows={5}
                value={getInputValue('body')}
                onChange={(e) => handleInputChange('body', e.target.value)}
                onBlur={handleInputBlur}
              />
            </QuoteField>
          </div>

          <div className="quote-form-row quote-form-section-divider">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Line items</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>

          <div className="quote-form-panel">
            <div className="quote-form-line-items-header" aria-hidden="true">
              <span className="quote-form-line-items-h-col quote-form-line-items-h-drag" />
              <span className="quote-form-line-items-h-col quote-form-line-items-h-product">Product</span>
              <span className="quote-form-line-items-h-col quote-form-line-items-h-desc">Description</span>
              <span className="quote-form-line-items-h-col quote-form-line-items-h-qty">Qty</span>
              <span className="quote-form-line-items-h-col quote-form-line-items-h-unit">Unit</span>
              <span className="quote-form-line-items-h-col quote-form-line-items-h-line">Line</span>
              <span className="quote-form-line-items-h-col quote-form-line-items-h-actions" />
            </div>

            <DndContext
              sensors={lineDragSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleLineDragEnd}
            >
              <SortableContext items={lineSortIds} strategy={verticalListSortingStrategy}>
            {visibleLineEntries.map(({ index: i, sortId }) => {
              const previewRow = previewFieldValues?.productPreviewLine === i;
              const previewGreyPrice =
                previewRow && previewFieldValues?.previewProductHasListPrice;
              const previewGreyLineTotal =
                previewRow && previewFieldValues?.previewProductHasLineTotalPreview;
              const rawPriceVal = getInputValue(`quote_lines.${i}.price`);
              const rawLtVal = getInputValue(`quote_lines.${i}.line_total`);
              const rawQForLine = getInputValue(`quote_lines.${i}.quantity`);
              let lineTotalEffective = rawLtVal;
              if ((rawLtVal === '' || rawLtVal == null) && hasUnitPriceRaw(rawPriceVal)) {
                const qn = parseQuantityRaw(rawQForLine);
                const pn = Number(String(rawPriceVal).replace(/,/g, ''));
                if (qn != null && qn > 0 && Number.isFinite(pn)) {
                  lineTotalEffective = (qn * pn).toFixed(2);
                }
              }
              const unitPriceDisplayed =
                unitPriceFocusLine === i ? rawPriceVal : formatPriceTwoDecimals(rawPriceVal);
              const lineTotalDisplayed =
                lineTotalFocusLine === i
                  ? rawLtVal
                  : formatPriceTwoDecimals(lineTotalEffective);
              return (
                <QuoteSortableLineRow key={`quote-line-${sortId}`} id={sortId}>
                  <QuoteField {...quoteFieldProps(`quote_lines.${i}.model`)} className="quote-field-wrap--grow">
                    <input
                      ref={(el) => {
                        if (el) lineModelInputRefs.current[i] = el;
                        else delete lineModelInputRefs.current[i];
                      }}
                      className={`quote-form-control-bordered ${previewRow ? 'replace-field-value' : ''}`}
                      type="text"
                      placeholder="Product"
                      value={getInputValue(`quote_lines.${i}.model`)}
                      onChange={(e) => {
                        setAutocomplete('product', e.target.value, i);
                        handleInputChange(`quote_lines.${i}.model`, e.target.value);
                      }}
                      onBlur={handleInputBlur}
                    />
                  </QuoteField>
                  <QuoteField {...quoteFieldProps(`quote_lines.${i}.description`)} className="quote-field-wrap--grow">
                    <input
                      className={`quote-form-control-bordered ${previewRow ? 'replace-field-value' : ''}`}
                      type="text"
                      placeholder="Description"
                      value={getInputValue(`quote_lines.${i}.description`)}
                      onChange={(e) => {
                        setAutocomplete('product', e.target.value, i);
                        handleInputChange(`quote_lines.${i}.description`, e.target.value);
                      }}
                      onBlur={handleInputBlur}
                    />
                  </QuoteField>
                  <QuoteField {...quoteFieldProps(`quote_lines.${i}.quantity`)} className="quote-field-wrap--qty">
                    <input
                      className="quote-form-control-bordered"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Qty"
                      value={getInputValue(`quote_lines.${i}.quantity`)}
                      onChange={(e) => {
                        const qv = e.target.value;
                        handleInputChange(`quote_lines.${i}.quantity`, qv);
                        const pRaw = getInputValue(`quote_lines.${i}.price`);
                        if (!hasUnitPriceRaw(pRaw)) return;
                        const qn = parseQuantityRaw(qv);
                        const pn = Number(String(pRaw).replace(/,/g, ''));
                        if (qn != null && qn > 0 && Number.isFinite(pn)) {
                          handleInputChange(`quote_lines.${i}.line_total`, (qn * pn).toFixed(2));
                        }
                      }}
                      onBlur={handleInputBlur}
                    />
                  </QuoteField>
                  <QuoteField {...quoteFieldProps(`quote_lines.${i}.price`)} className="quote-field-wrap--money">
                    <input
                      className={`quote-form-control-bordered ${previewGreyPrice ? 'replace-field-value' : ''}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="Unit $"
                      value={unitPriceDisplayed}
                      onFocus={() => syncUnitPriceFormatOnFocus(i)}
                      onChange={(e) => {
                        const v = e.target.value;
                        const trimmed = (v || '').trim();
                        if (trimmed === '') {
                          clearUnitPrice(i);
                          return;
                        }
                        handleInputChange(`quote_lines.${i}.price`, v);
                        const pn = parseFloat(trimmed.replace(/,/g, ''));
                        if (!Number.isFinite(pn)) return;
                        const qn = parseQuantityRaw(getInputValue(`quote_lines.${i}.quantity`));
                        if (qn != null && qn > 0) {
                          handleInputChange(`quote_lines.${i}.line_total`, (qn * pn).toFixed(2));
                        }
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v === '') {
                          clearUnitPrice(i);
                        } else {
                          const n = parseFloat(v.replace(/,/g, ''));
                          if (Number.isFinite(n)) {
                            handleInputChange(`quote_lines.${i}.price`, n.toFixed(2));
                            syncLineTotalFromUnitPrice(i);
                          }
                        }
                        setUnitPriceFocusLine((cur) => (cur === i ? null : cur));
                        handleInputBlur();
                      }}
                    />
                  </QuoteField>
                  <QuoteField {...quoteFieldProps(`quote_lines.${i}.line_total`)} className="quote-field-wrap--money">
                    <input
                      className={`quote-form-control-bordered ${previewGreyLineTotal ? 'replace-field-value' : ''}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="Line $"
                      value={lineTotalDisplayed}
                      onFocus={() => syncLineTotalFormatOnFocus(i)}
                      onChange={(e) => {
                        handleInputChange(`quote_lines.${i}.line_total`, e.target.value);
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v === '') {
                          const pRaw = getInputValue(`quote_lines.${i}.price`);
                          if (hasUnitPriceRaw(pRaw)) {
                            const qn = parseQuantityRaw(getInputValue(`quote_lines.${i}.quantity`));
                            const pn = Number(String(pRaw).replace(/,/g, ''));
                            if (qn != null && qn > 0 && Number.isFinite(pn)) {
                              handleInputChange(`quote_lines.${i}.line_total`, (qn * pn).toFixed(2));
                            }
                          } else {
                            handleInputChange(`quote_lines.${i}.line_total`, null);
                          }
                        } else {
                          const ln = parseFloat(v.replace(/,/g, ''));
                          if (Number.isFinite(ln)) {
                            handleInputChange(`quote_lines.${i}.line_total`, ln.toFixed(2));
                            const pRaw = getInputValue(`quote_lines.${i}.price`);
                            if (hasUnitPriceRaw(pRaw)) {
                              const qn = parseQuantityRaw(getInputValue(`quote_lines.${i}.quantity`));
                              if (qn != null && qn > 0) {
                                handleInputChange(`quote_lines.${i}.price`, (ln / qn).toFixed(2));
                              }
                            }
                          }
                        }
                        setLineTotalFocusLine((cur) => (cur === i ? null : cur));
                        handleInputBlur();
                      }}
                    />
                  </QuoteField>
                  <button
                    type="button"
                    className="quote-form-button remove-product-button"
                    onClick={() => removeQuoteLine(i)}
                    aria-label="Remove line item"
                  >
                    X
                  </button>
                </QuoteSortableLineRow>
              );
            })}
              </SortableContext>
            </DndContext>
            {renderSearchResults('product')}

            <div className="quote-form-row quote-form-line-items-add-row">
              <button type="button" className="quote-form-button add-product-button" onClick={addQuoteLine}>
                + Add line item
              </button>
            </div>
          </div>

          <div className="quote-form-row quote-form-section-divider">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Additional services</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>
          <div className="quote-form-panel">
            <p className="quote-form-services-hint">
              Enter services separated by commas. They appear as separate lines on the customer-facing quote.
            </p>
            <div className="quote-form-bordered-block quote-form-additional-services">
              <label className="quote-form-subsection-label" htmlFor="quote-additional-services-included">
                Included
              </label>
              <QuoteField {...quoteFieldProps('additional_services_included')} className="quote-field-wrap--block">
                <textarea
                  id="quote-additional-services-included"
                  className="quote-form-control-bordered"
                  placeholder="e.g. Delivery, On-site installation"
                  maxLength={255}
                  rows={3}
                  value={getInputValue('additional_services_included')}
                  onChange={(e) => handleInputChange('additional_services_included', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
              <label className="quote-form-subsection-label" htmlFor="quote-additional-services-excluded">
                Not included
              </label>
              <QuoteField {...quoteFieldProps('additional_services_excluded')} className="quote-field-wrap--block">
                <textarea
                  id="quote-additional-services-excluded"
                  className="quote-form-control-bordered"
                  placeholder="e.g. Breadsticks, Dipping sauces"
                  maxLength={255}
                  rows={3}
                  value={getInputValue('additional_services_excluded')}
                  onChange={(e) => handleInputChange('additional_services_excluded', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
            </div>
          </div>

          <div className="quote-form-row quote-form-section-divider">
            <div className="quote-form-section-divider-line" aria-hidden="true" />
            <span className="quote-form-section-title">Terms</span>
            <div className="quote-form-section-divider-line" aria-hidden="true" />
          </div>

          <div className="quote-form-panel">
            <div className="quote-form-row quote-form-row--split">
              <div className="quote-form-split-field">
                <label className="quote-form-subsection-label" htmlFor="quote-lead-time">
                  Lead time <span className="quote-form-label-optional">(optional)</span>
                </label>
                <QuoteField {...quoteFieldProps('lead_time')} className="quote-field-wrap--block">
                  <input
                    id="quote-lead-time"
                    className="quote-form-control-bordered"
                    type="text"
                    placeholder="e.g. 4–6 weeks"
                    value={getInputValue('lead_time')}
                    onChange={(e) => handleInputChange('lead_time', e.target.value)}
                    onBlur={handleInputBlur}
                  />
                </QuoteField>
              </div>
              <div className="quote-form-split-field">
                <label className="quote-form-subsection-label" htmlFor="quote-valid-days">
                  Days valid
                </label>
                <QuoteField {...quoteFieldProps('valid_days')} className="quote-field-wrap--block">
                  <input
                    id="quote-valid-days"
                    className="quote-form-control-bordered"
                    type="number"
                    min={1}
                    placeholder="90"
                    value={getInputValue('valid_days')}
                    onChange={(e) => handleInputChange('valid_days', e.target.value)}
                    onBlur={(e) => {
                      const raw = String(e.target.value).trim();
                      const n = parseInt(raw, 10);
                      const next =
                        raw === '' || !Number.isFinite(n) || n < 1 ? '90' : String(n);
                      handleInputChange('valid_days', next);
                      handleInputBlur();
                    }}
                  />
                </QuoteField>
              </div>
            </div>
            {isAdmin && (
              <div>
                <label className="quote-form-subsection-label" htmlFor="quote-proasys-signature">
                  ProAsys signature
                </label>
                <QuoteField {...quoteFieldProps('proasys_signature')} className="quote-field-wrap--block">
                  <select
                    id="quote-proasys-signature"
                    className="quote-form-control-bordered browser-default"
                    value={String(selectedProasysSignerId)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      handleInputChange('proasys_signature', raw === '' ? null : Number(raw));
                      quoteUpdateTimerRef.current = setTimeout(() => persistQuote(), 400);
                    }}
                  >
                    {!selectedProasysSignerId && (
                      <option value="">Select signer</option>
                    )}
                    {proasysSignerOptions.map((emp) => (
                      <option key={emp.id} value={String(emp.id)}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </QuoteField>
              </div>
            )}
            <div>
              <label className="quote-form-subsection-label" htmlFor="quote-internal-notes">
                Internal notes <span className="quote-form-label-optional">(optional)</span>
              </label>
              <QuoteField {...quoteFieldProps('notes')} className="quote-field-wrap--block">
                <textarea
                  id="quote-internal-notes"
                  className="quote-form-control-bordered"
                  placeholder="Not shown on the customer-facing quote."
                  maxLength={255}
                  rows={3}
                  value={getInputValue('notes')}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  onBlur={handleInputBlur}
                />
              </QuoteField>
            </div>
          </div>
        </form>
      </div>
      <div className="quote-wizard-footer">
        <i>ProAsys • 318 Hendel St • Shillington, PA 19607-2495 • ph: 610-775-1505 • fax: 610-775-8776</i>
      </div>
      <div className="quote-wizard-buttons-container">
        <button
          type="button"
          className="quote-wizard-footer-button preview-button"
          onClick={() => {
            cancelPendingFlush();
            persistQuote((saved) => {
              const hash = (saved && saved.hash_id) || activeQuote.hash_id;
              if (!hash) {
                window.alert('Save the quote first to open a preview.');
                return;
              }
              window.open(
                `${QUOTE_PUBLIC_SHOW_BASE}?id=${encodeURIComponent(hash)}`,
                '_blank',
                'noopener,noreferrer'
              );
            });
          }}
        >
          Preview
        </button>
        <button
          type="button"
          className="quote-wizard-footer-button send-button"
          onClick={() => {
            cancelPendingFlush();
            persistQuote((saved) => {
              const hash = (saved && saved.hash_id) || activeQuote.hash_id;
              if (!hash) {
                window.alert('Save the quote before sending.');
                return;
              }
              const recipientSource =
                saved && saved.recipients != null && saved.recipients !== ''
                  ? saved.recipients
                  : getInputValue('recipients');
              setRecipientModalIsSendFlow(true);
              setSelectedRecipients(parseRecipientsCsv(recipientSource));
              setRecipientModalOpen(true);
            });
          }}
        >
          Send
        </button>
      </div>

      {recipientModalOpen && (
        <RecipientSelect
          contacts={recipientCustomerContacts.filter((c) => c.email)}
          selectedRecipients={selectedRecipients}
          handleRecipientSelect={(email) => {
            setSelectedRecipients((prev) =>
              prev.includes(email) ? prev.filter((r) => r !== email) : [...prev, email]
            );
          }}
          addNewContact={(email) => {
            setSelectedRecipients((prev) => (prev.includes(email) ? prev : [...prev, email]));
          }}
          submit={recipientModalIsSendFlow ? completeSend : applyRecipientsFromModal}
          close={() => {
            setRecipientModalOpen(false);
            setRecipientModalIsSendFlow(false);
            setSelectedRecipients([]);
          }}
          loading={props.loading}
          loadingMessage={props.loadingMessage}
        />
      )}
    </div>

  )
}

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    activeQuote: state.activeQuote,
    products: state.quotableProducts,
    customers: state.customers,
    quoteEmployees: state.quoteEmployees,
    user: state.user,
    loading: state.loading,
    loadingMessage: state.loadingMessage
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(QuoteWizard);
